import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material';

// @import Custom Components
import { WriterlyFormikField } from '@shared/Formik/FormikField';

// @import Hooks
import useDisabledActiveColor from '@hooks/useDisabledActiveColor';

// @import Paths
import { guestPaths } from '@routes/paths';

// @import Utils
import { renderAuthFormLabel, renderAuthPlaceholder } from '@shared/Auth/Utils';

export const WriterlyAuthForm = ({
  formFields,
  formValidation,
  validationSchema,
  handleSubmit,
  formPage,
  extraFields,
  buttons,
  customFieldsSx = {},
  customFieldsProps = {},
  passwordIconClasses = '',
}) => {
  const { pathname } = useLocation();
  const theme = useTheme();
  const { ref } = useDisabledActiveColor();

  const passwordInputs = ['password', 'confirm_password'];
  const disabledEmailPaths = [
    guestPaths.joinYourTeam,
    guestPaths.signUpInvitation,
  ];

  const getDisableField = (field) => {
    return (
      disabledEmailPaths.some((path) => pathname.includes(path)) &&
      field === 'email'
    );
  };

  return (
    <Formik
      initialValues={formFields}
      validate={formValidation}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, setFieldValue, values, handleBlur, setValues }) => {
        const hasDefaultValues = Object.values(formFields).some((val) => !!val);

        useEffect(() => {
          if (hasDefaultValues) {
            setValues(formFields);
          }
        }, [hasDefaultValues]);

        return (
          <Form autoComplete={'off'}>
            <div className="flex flex-col gap-7">
              {Object.keys(formFields).map((_field, index) => {
                const disabledField = getDisableField(_field);
                const label = renderAuthFormLabel(_field, formPage);
                const placeholder = renderAuthPlaceholder(_field, formPage);
                return (
                  <div key={index}>
                    {label && (
                      <label
                        className="text-sm"
                        style={{ color: theme.palette.text.secondary }}
                        htmlFor={_field}
                      >
                        {label}{' '}
                        <span style={{ color: theme.palette.error.main }}>
                          *
                        </span>
                      </label>
                    )}
                    <WriterlyFormikField
                      name={_field}
                      error={errors[_field]}
                      touched={touched[_field]}
                      value={values[_field]}
                      onChange={(e) =>
                        setFieldValue(`${_field}`, e.target.value)
                      }
                      placeholder={placeholder}
                      type={
                        passwordInputs.includes(_field) ? 'password' : 'text'
                      }
                      onBlur={handleBlur}
                      disabled={disabledField}
                      customSx={customFieldsSx}
                      inputRef={disabledField ? ref : null}
                      passwordIconClasses={passwordIconClasses}
                      {...customFieldsProps}
                    />
                  </div>
                );
              })}
              {extraFields}
            </div>
            <div className="buttons flex flex-col justify-center mt-3">
              {buttons}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

WriterlyAuthForm.propTypes = {
  formFields: PropTypes.any.isRequired,
  formValidation: PropTypes.func,
  validationSchema: PropTypes.any,
  handleSubmit: PropTypes.func.isRequired,
  formPage: PropTypes.string.isRequired,
  extraFields: PropTypes.node,
  buttons: PropTypes.node.isRequired,
  customFieldsSx: PropTypes.object,
  customFieldsProps: PropTypes.object,
  passwordIconClasses: PropTypes.string,
};
